/**
 * Branding Service
 *
 * Handles all branding-related API calls including:
 * - Fetching branding configuration
 * - Uploading company logos
 * - Updating brand colors
 */

import apiClient from './api/client';

export interface BrandingConfig {
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  logo_url: string;
  pdf_logo_url?: string;
  company_name: string;
  system_title: string;
  system_title_en?: string;
  system_title_ar?: string;
  /** Public self-registration toggle — defaults to true when the API omits it. */
  registration_enabled?: boolean;
  /** Login page: the designed MDC header (default) vs the uploaded logo. */
  login_design_lockup?: boolean;
  /** DEFAULT_LANGUAGE — what a first-time visitor sees. */
  default_language?: 'ar' | 'en';
  /** Login page content; '' / missing = the design default. */
  login_header_url?: string;
  login_background_url?: string;
  login_welcome_ar?: string;
  login_welcome_en?: string;
  login_features_ar?: string[];
  login_features_en?: string[];
}

export type BrandingImageType = 'platform' | 'pdf' | 'login_header' | 'login_background';

class BrandingService {
  /**
   * Get current branding configuration (public endpoint, no auth required)
   */
  async getBrandingConfig(): Promise<BrandingConfig> {
    try {
      // 10s, not 3s: a cold-started Cloud Run instance takes ~7s to first
      // byte, and a timeout here used to drop users onto the legacy MDC
      // fallback (black theme + MDC logo) until the next successful fetch.
      const response = await apiClient.get('/system/branding/', { timeout: 10000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching branding config:', error);
      // Nuagent defaults on error — must match BrandingContext's initial
      // state so a failed fetch is visually indistinguishable from default.
      return {
        primary_color: '#12717A',
        secondary_color: '#168a96',
        accent_color: '#16a34a',
        logo_url: '/nuagent-logo.png',
        company_name: 'Nuagent',
        system_title: 'Business Operations',
        system_title_en: 'Business Operations',
        system_title_ar: 'إدارة الأعمال',
        registration_enabled: true
      };
    }
  }

  /**
   * Upload a new company logo (admin only)
   * @param logoFile - The logo file to upload
   * @param logoType - 'platform' for web app logo, 'pdf' for PDF document logo
   */
  async uploadLogo(
    logoFile: File,
    logoType: BrandingImageType = 'platform'
  ): Promise<{ success: boolean; logo_url: string; logo_type: string; message?: string; error?: string }> {
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);
      formData.append('logo_type', logoType);

      // Don't set Content-Type header manually - axios will set it with proper boundary
      const response = await apiClient.post('/system/upload-logo/', formData);

      return response.data;
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      throw new Error(error.response?.data?.error || 'Failed to upload logo');
    }
  }

  /**
   * Update branding colors (admin only)
   */
  async updateBrandingColors(colors: {
    BRAND_PRIMARY_COLOR?: string;
    BRAND_SECONDARY_COLOR?: string;
    BRAND_ACCENT_COLOR?: string;
  }): Promise<any> {
    try {
      const response = await apiClient.post('/system/settings/bulk_update/', {
        settings: colors
      });
      return response.data;
    } catch (error: any) {
      console.error('Error updating branding colors:', error);
      throw new Error(error.response?.data?.error || 'Failed to update colors');
    }
  }

  /**
   * Update other branding settings by key (admin only), e.g. LOGIN_USE_DESIGN_LOCKUP
   */
  async updateBrandingSettings(settings: Record<string, string>): Promise<any> {
    try {
      const response = await apiClient.post('/system/settings/bulk_update/', { settings });
      return response.data;
    } catch (error: any) {
      console.error('Error updating branding settings:', error);
      throw new Error(error.response?.data?.error || 'Failed to update settings');
    }
  }

  /**
   * Reset branding to defaults (admin only)
   */
  async resetBrandingToDefaults(): Promise<any> {
    try {
      const response = await apiClient.post('/system/settings/reset_to_defaults/', {
        setting_keys: [
          'BRAND_PRIMARY_COLOR',
          'BRAND_SECONDARY_COLOR',
          'BRAND_ACCENT_COLOR',
          'BRAND_LOGO_URL'
        ]
      });
      return response.data;
    } catch (error: any) {
      console.error('Error resetting branding:', error);
      throw new Error(error.response?.data?.error || 'Failed to reset branding');
    }
  }
}

export default new BrandingService();
